import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, BRAND } from "../data/content";
import { MagneticButton } from "./MagneticButton";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color,padding] duration-500 ${
          scrolled ? "bg-white/80 backdrop-blur-xl border-b border-brand-ink/10 py-2.5" : "bg-transparent border-b border-transparent py-3.5"
        }`}
      >
        <nav className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <img src={BRAND.logo} alt="Tangensis Business Solutions" className="h-9 w-auto md:h-11 transition-transform duration-300 group-hover:scale-105" />
          </Link>

          <div className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((l) => {
              const active = location.pathname === l.path;
              return (
                <Link
                  key={l.path}
                  to={l.path}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={`relative text-sm font-semibold tracking-[0.15em] transition-colors duration-300 ${active ? "text-brand-ink" : "text-brand-ink/75 hover:text-brand-primary"}`}
                >
                  {l.label}
                  {active && <motion.span layoutId="nav-underline" className="absolute -bottom-2 left-0 right-0 h-0.5 gradient-bg rounded-full" />}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <MagneticButton
              as="a"
              href="/contact"
              onClick={(e) => { e.preventDefault(); window.location.href = "/contact"; }}
              data-testid="nav-cta-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-full gradient-bg text-white text-sm font-semibold px-5 py-2.5 shadow-lg shadow-brand-primary/30 hover:shadow-brand-primary/50 transition-shadow duration-300"
            >
              LET&apos;S TALK <ArrowUpRight className="h-4 w-4" />
            </MagneticButton>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              className="lg:hidden text-brand-ink p-2"
              aria-label="Open menu"
            >
              <Menu className="h-7 w-7" />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] bg-white"
            data-testid="mobile-menu-overlay"
          >
            <div className="absolute inset-0 gradient-bg opacity-20" />
            <div className="relative h-full flex flex-col p-6 md:p-12">
              <div className="flex items-center justify-between">
                <img src={BRAND.logo} alt="Tangensis" className="h-11 w-auto" />
                <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" className="text-brand-ink p-2" aria-label="Close menu">
                  <X className="h-8 w-8" />
                </button>
              </div>
              <div className="flex-1 flex flex-col justify-center gap-2">
                {NAV_LINKS.map((l, i) => (
                  <motion.div
                    key={l.path}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Link
                      to={l.path}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                      className="font-heading font-extrabold text-5xl sm:text-6xl text-brand-ink hover:text-brand-orange transition-colors duration-300 block py-1"
                    >
                      {l.label}
                    </Link>
                  </motion.div>
                ))}
              </div>
              <Link
                to="/contact"
                data-testid="mobile-cta-button"
                className="inline-flex items-center justify-center gap-2 rounded-full gradient-bg text-white text-base font-bold px-6 py-4"
              >
                LET&apos;S TALK <ArrowUpRight className="h-5 w-5" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
